/**
 * Persisted quick-form / catalog assessment instances per resident (demo / Phase 1).
 * Key: clinical_assessment_instances → { [residentId]: Array<StoredInstance> }
 */

import LocalStorageService from '../services/storage/LocalStorageService';
import { clinicalAssessmentCatalog } from '../data/clinicalAssessmentCatalog';

const STORAGE_KEY = 'clinical_assessment_instances';

/** @typedef {{ id: string, residentId: string|number, savedAt: string, payload: object, card: object }} StoredInstance */

function normalizeStore(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw;
  return {};
}

/**
 * @param {string|number} residentId
 * @returns {StoredInstance[]}
 */
export function getInstancesForResident(residentId) {
  const rid = String(residentId);
  const all = normalizeStore(LocalStorageService.get(STORAGE_KEY, {}));
  const list = all[rid];
  return Array.isArray(list) ? list : [];
}

/**
 * @param {string|number} residentId
 * @returns {object[]} Assessment cards for UI (same shape as profileData.assessments entries)
 */
export function getPersistedAssessmentCards(residentId) {
  return getInstancesForResident(residentId).map((row) => row.card).filter(Boolean);
}

function catalogTypeForSlug(slug) {
  const hit = clinicalAssessmentCatalog.find((a) => a.slug === slug);
  return hit ? hit.domain : 'Clinical';
}

function formatDate(iso) {
  try {
    return (iso || '').slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

/**
 * Map quick-form payload to recorded-assessment card shape used in Residents UI.
 * @param {object} payload
 * @param {{ id?: string|number, name?: string }} resident
 */
export function quickFormPayloadToAssessmentCard(payload, resident) {
  if (!payload || typeof payload !== 'object') return null;

  const slug = payload.slug || 'unknown';
  const savedAt = payload.savedAt || new Date().toISOString();
  const id = `qa-${slug}-${savedAt.replace(/[:.]/g, '')}`;

  if (payload.tool === '4AT' || slug === 'delirium-4at') {
    const total = payload.scores?.total ?? '—';
    return {
      id,
      name: 'Delirium Screening (4AT)',
      type: catalogTypeForSlug('delirium-4at'),
      date: formatDate(savedAt),
      assessor: 'Quick form (demo)',
      status: 'Completed',
      score: total,
      maxScore: 12,
      findings: payload.interpretation || '4AT completed via quick form.',
      results: payload.scores ? `Scores A–D: ${JSON.stringify(payload.scores)}` : '—',
      recommendations: 'Follow local delirium protocol if elevated.',
      nextReview: null,
    };
  }

  if (payload.tool === 'catalog-quick-form') {
    const name = payload.assessmentName || slug;
    const valuesStr =
      payload.values && typeof payload.values === 'object'
        ? Object.entries(payload.values)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ')
        : '—';
    return {
      id,
      name,
      type: catalogTypeForSlug(slug),
      date: formatDate(savedAt),
      assessor: 'Catalog quick form (demo)',
      status: 'Completed',
      score: null,
      maxScore: null,
      findings: valuesStr.slice(0, 500) + (valuesStr.length > 500 ? '…' : ''),
      results: 'Captured via assessment library quick form.',
      recommendations: 'Complete full assessment per assessment specifications where required.',
      nextReview: null,
    };
  }

  return {
    id,
    name: String(payload.tool || 'Assessment'),
    type: catalogTypeForSlug(slug),
    date: formatDate(savedAt),
    assessor: 'Quick form (demo)',
    status: 'Completed',
    findings: JSON.stringify(payload).slice(0, 400),
    results: '—',
    recommendations: '—',
    nextReview: null,
  };
}

/**
 * @param {string|number} residentId
 * @param {object} payload
 * @param {object} card
 */
export function appendInstance(residentId, payload, card) {
  const rid = String(residentId);
  const all = normalizeStore(LocalStorageService.get(STORAGE_KEY, {}));
  const list = Array.isArray(all[rid]) ? [...all[rid]] : [];
  const savedAt = payload?.savedAt || new Date().toISOString();
  list.push({
    id: card?.id || `qa-${Date.now()}`,
    residentId: rid,
    savedAt,
    payload,
    card,
  });
  all[rid] = list;
  LocalStorageService.set(STORAGE_KEY, all);
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.debug('[clinicalAssessmentInstances] appendInstance', { residentId: rid, count: list.length, slug: payload?.slug });
  }
}
